import {
  Accordion,
  AccordionButton,
  AccordionItem,
  Box,
  Flex,
  Grid,
  Icon,
  Tag,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi";

import { CaretIcon } from "../../../../components";
import { useSendGAEvent } from "../../../../utils/googleAnalytics";
import {
  CallSpeakerFragment,
  CallSpeakerOption,
  TranscriptSegmentFragment,
} from "../../../graphql";
import useCandidateOnlyPlayback from "../../MediaPlayer/useCandidateOnlyPlayback";
import { MediaPlayerInterface } from "../../MediaPlayer/useMediaPlayer";
import CandidateOnlyToggleV2 from "./CandidateOnlyToggleV2";
import SpeakerBarSegmentV2 from "./SpeakerBarSegmentV2";
import SpeakerBarV2 from "./SpeakerBarV2";
import SpeakerTagV2 from "./SpeakerTagV2";
import {
  CondensedSpeaker,
  condenseSpeakerList,
  sortSpeakerList,
} from "./utils";

interface CommonSpeakerBarProps {
  callId?: string;
  callDuration: number;
  speakers: CondensedSpeaker[];
  speakerOptions?: Pick<CallSpeakerOption, "id" | "label">[];
  canChangeSpeaker?: boolean;
  segments: TranscriptSegmentFragment[];
  onClickSegment: (startTime: number) => void;
}

interface SubSpeakerBarProps extends CommonSpeakerBarProps {
  candidateIdentified: boolean;
  candidateOnly: boolean;
  setCandidateOnly(candidateOnly: boolean): void;
}

const SpeakerBarsClosedV2: React.FC<
  SubSpeakerBarProps & { canExpandSpeakerBars?: boolean }
> = ({
  callId,
  callDuration,
  candidateIdentified,
  candidateOnly,
  setCandidateOnly,
  speakers,
  speakerOptions,
  canChangeSpeaker,
  canExpandSpeakerBars = true,
  segments,
  onClickSegment,
}) => {
  const speakerBarSegments = useMemo(() => {
    return segments.map((segment) => {
      const speaker = speakers.find((s) => s.speakerTag === segment.speakerTag);
      if (!speaker) {
        return null;
      }
      return (
        <SpeakerBarSegmentV2
          candidateOnly={candidateOnly}
          key={segment.id}
          speaker={speaker}
          segment={segment}
          callDuration={callDuration}
          onClickSegment={onClickSegment}
        />
      );
    });
  }, [segments, speakers, candidateOnly, callDuration, onClickSegment]);
  const finalSpeakers =
    speakers.length > 4 ? condenseSpeakerList(speakers) : speakers;
  const firstUnidentifiedSpeaker = speakers.find((s) => !s.identified);
  const { isOpen: ctaClicked, onOpen: hideCta } = useDisclosure();
  const showCta =
    canChangeSpeaker &&
    segments.length > 0 &&
    !ctaClicked &&
    !!firstUnidentifiedSpeaker;

  return (
    <>
      <Grid
        alignItems="center"
        templateColumns="minmax(0, 1fr) auto auto"
        width="100%"
      >
        <Flex minWidth="0" gap="3" mr="2" ml="2">
          {showCta ? (
            <Tag
              fontSize="xs"
              borderRadius="xl"
              variant="info"
              cursor="pointer"
              onClick={hideCta}
              lineHeight="4"
              fontWeight="medium"
            >
              <Icon
                as={HiOutlineInformationCircle}
                mr="1"
                color="gray.900"
                boxSize="4"
              />
              Complete this interview by identifying each speaker in the
              conversation
            </Tag>
          ) : (
            finalSpeakers.map((speaker) => (
              <SpeakerTagV2
                key={speaker.id}
                callId={callId}
                candidateOnly={candidateOnly}
                speaker={speaker}
                canChangeSpeaker={canChangeSpeaker && !speaker.condensed}
                speakerOptions={speakerOptions}
                defaultIsOpen={
                  ctaClicked && speaker.id === firstUnidentifiedSpeaker?.id
                }
              />
            ))
          )}
        </Flex>

        {candidateIdentified ? (
          <CandidateOnlyToggleV2
            candidateOnly={candidateOnly}
            setCandidateOnly={setCandidateOnly}
            mr="4"
          />
        ) : (
          <Box />
        )}
        {canExpandSpeakerBars && finalSpeakers.length > 1 && (
          <AccordionButton
            p={0}
            width="6"
            h="6"
            justifyContent="center"
            alignItems="center"
            aria-label="Show all speaker bars"
          >
            <Tooltip label="Show all speaker bars" shouldWrapChildren>
              <Icon
                as={CaretIcon}
                data-tour-id="ip2-speaker-bar-toggle"
                color="gray.100"
                boxSize="4"
                transition="all 0.5s ease"
                style={{ rotate: "0deg", marginTop: "-2px" }}
              />
            </Tooltip>
          </AccordionButton>
        )}
      </Grid>
      <Flex
        position="relative"
        alignItems="center"
        bg="whiteAlpha.200"
        borderRadius="4px"
        h="16px"
        p="6px"
        mt={2}
        w="100%"
      >
        {speakerBarSegments}
      </Flex>
    </>
  );
};

const SpeakerBarsOpenV2: React.FC<SubSpeakerBarProps> = ({
  callId,
  callDuration,
  candidateIdentified,
  candidateOnly,
  setCandidateOnly,
  speakers,
  speakerOptions,
  canChangeSpeaker,
  segments,
  onClickSegment,
}) => {
  const sendGAEvent = useSendGAEvent();
  useEffect(() => {
    sendGAEvent("speaker_bar_open", "call_review");
  }, []);

  const segmentsBySpeakerTag = speakers.reduce((sum, speaker) => {
    return {
      ...sum,
      [speaker.speakerTag]: segments.filter(
        (segment) => segment.speakerTag === speaker.speakerTag
      ),
    };
  }, {} as Record<number, TranscriptSegmentFragment[]>);

  return (
    <>
      <Grid
        alignItems="center"
        templateColumns="minmax(0, 1fr) auto auto"
        width="100%"
      >
        <Flex minWidth="0" mr="2" ml="2">
          <SpeakerTagV2
            callId={callId}
            candidateOnly={candidateOnly}
            speaker={speakers[0]}
            canChangeSpeaker={canChangeSpeaker}
            speakerOptions={speakerOptions}
          />
        </Flex>

        {candidateIdentified ? (
          <CandidateOnlyToggleV2
            candidateOnly={candidateOnly}
            setCandidateOnly={setCandidateOnly}
            mr="4"
          />
        ) : (
          <Box />
        )}
        <AccordionButton
          flexGrow="0.95"
          width="6"
          h="6"
          justifyContent="center"
          alignItems="center"
          p={0}
        >
          <Tooltip label="Hide all speaker bars" shouldWrapChildren>
            <Icon
              as={CaretIcon}
              color="gray.100"
              boxSize="4"
              transition="all 0.5s ease"
              style={{ rotate: "180deg", marginTop: "-2px" }}
            />
          </Tooltip>
        </AccordionButton>
      </Grid>
      <SpeakerBarV2
        callDuration={callDuration}
        candidateOnly={candidateOnly}
        speaker={speakers[0]}
        segments={segmentsBySpeakerTag[speakers[0].speakerTag]}
        onClickSegment={onClickSegment}
        ml="0"
      />
      {speakers.slice(1).map((speaker) => (
        <VStack key={speaker.id} align="flex-start" spacing={2} mt={5}>
          <Flex ml="2">
            <SpeakerTagV2
              callId={callId}
              candidateOnly={candidateOnly}
              speaker={speaker}
              canChangeSpeaker={canChangeSpeaker}
              speakerOptions={speakerOptions}
            />
          </Flex>
          <SpeakerBarV2
            callDuration={callDuration}
            candidateOnly={candidateOnly}
            speaker={speaker}
            segments={segmentsBySpeakerTag[speaker.speakerTag]}
            onClickSegment={onClickSegment}
            ml="0"
          />
        </VStack>
      ))}
    </>
  );
};

export interface SpeakerBarsV2Props
  extends Omit<CommonSpeakerBarProps, "speakers"> {
  player: Pick<MediaPlayerInterface, "seek" | "time">;
  canExpandSpeakerBars?: boolean;
  speakers: CallSpeakerFragment[];
  /** For storybook: force speaker bars to start expanded */
  defaultIndex?: number;
}

const SpeakerBarsV2: React.FC<SpeakerBarsV2Props> = ({
  player,
  segments,
  speakers,
  canExpandSpeakerBars,
  defaultIndex,
  ...rest
}) => {
  const sortedSpeakers = useMemo(() => sortSpeakerList(speakers), [speakers]);
  const { seek, time } = player;
  const { candidateOnly, setCandidateOnly, candidateIdentified } =
    useCandidateOnlyPlayback(speakers, time, seek, segments);

  return (
    <Accordion allowToggle defaultIndex={defaultIndex}>
      <AccordionItem border="0">
        {({ isExpanded }) =>
          isExpanded ? (
            <SpeakerBarsOpenV2
              candidateIdentified={candidateIdentified}
              candidateOnly={candidateOnly}
              segments={segments}
              setCandidateOnly={setCandidateOnly}
              speakers={sortedSpeakers}
              {...rest}
            />
          ) : (
            <SpeakerBarsClosedV2
              candidateIdentified={candidateIdentified}
              candidateOnly={candidateOnly}
              segments={segments}
              setCandidateOnly={setCandidateOnly}
              speakers={sortedSpeakers}
              canExpandSpeakerBars={canExpandSpeakerBars}
              {...rest}
            />
          )
        }
      </AccordionItem>
    </Accordion>
  );
};

export default SpeakerBarsV2;
